import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const slides = [
  {
    id: 1,
    title: "Celebrating 100 Years",
    subtitle: "of Protecting Our Countryside",
    description: "Join us in celebrating a century of conservation, community, and countryside protection across Rutland and Lincolnshire.",
    cta: "Explore Our Centenary",
    ctaLink: "/centenary",
    image: "/images/cpre images for website/5.jpg",
  },
  {
    id: 2,
    title: "Become a Volunteer",
    subtitle: "Make a Difference Today",
    description: "Help protect the landscapes you love. From countryside walks to conservation projects, there's a role for everyone.",
    cta: "Get Involved",
    ctaLink: "/volunteering",
    image: "/images/cpre images for website/9.jpg",
  },
  {
    id: 3,
    title: "Local Countryside Projects",
    subtitle: "Conservation in Action",
    description: "Discover how we're restoring habitats, protecting wildlife, and maintaining the rural character of our region.",
    cta: "View Projects",
    ctaLink: "/projects",
    image: "/images/cpre images for website/11.jpg",
  },
];

export const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  return (
    <section 
      className="relative h-[600px] md:h-[700px] lg:h-[800px] overflow-hidden"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      data-testid="hero-carousel"
    >
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          </div>

          {/* Content */}
          <div className="relative h-full container-custom flex items-center">
            <div className="max-w-2xl text-white">
              <span className="inline-block px-4 py-2 bg-cpre-harvest-gold text-cpre-new-yew rounded-full text-sm font-medium mb-6 opacity-0 animate-fade-in" style={{ animationDelay: '200ms' }}>
                {slide.subtitle}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 opacity-0 animate-fade-in text-white" style={{ animationDelay: '400ms' }}>
                {slide.title}
              </h1>
              <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed opacity-0 animate-fade-in" style={{ animationDelay: '600ms' }}>
                {slide.description}
              </p>
              <div className="opacity-0 animate-fade-in" style={{ animationDelay: '800ms' }}>
                <Link to={slide.ctaLink}>
                  <Button 
                    size="lg" 
                    className="bg-cpre-new-yew hover:bg-cpre-hill text-white rounded-full px-8 h-14 text-base"
                    data-testid={`hero-cta-${index}`}
                  >
                    {slide.cta}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Dots - for navigation */}
      <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? 'bg-white w-8' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
            data-testid={`hero-dot-${index}`}
          />
        ))}
      </div>
    </section>
  );
};
